<i18n>
{
  "fr": {
    "bold-button": "Gras",
    "italic-button": "Italique",
    "link-button": "Lien",
    "h3-button": "Entête niveau 3",
    "h4-button": "Entête niveau 4",
    "h5-button": "Entête niveau 5",
    "ul-button": "Liste à puces",
    "ol-button": "Liste numérotée",

    "link-url-label": "URL du lien",
    "link-set-tooltip": "Appliquer le lien",
    "link-remove-tooltip": "Retirer le lien",
    "link-cancel": "Annuler"
  },
  "en": {
    "bold-button": "Bold",
    "italic-button": "Italic",
    "link-button": "Link",
    "h3-button": "Level 3 heading",
    "h4-button": "Level 4 heading",
    "h5-button": "Level 5 heading",
    "ul-button": "Bullet list",
    "ol-button": "Numbered list",

    "link-url-label": "Link URL",
    "link-set-tooltip": "Set link",
    "link-remove-tooltip": "Remove link",
    "link-cancel": "Cancel"
  }
}
</i18n>

<template>
  <div>
    <editor-menu-bar :editor="editor" v-slot="{ commands, isActive, getMarkAttrs }">
      <div class="mb-12px">
        <!-- Edit link form -->
        <form
          v-if="linkMenuActive"
          @submit.prevent="
            commands.link({ href: linkUrl })
            linkMenuActive = false
          "
          @keydown.esc="linkMenuActive = false"
        >
          <b-field :label="$t('link-url-label')" label-position="on-border" addons>
            <div class="control" style="flex: 1 1 auto">
              <b-input type="url" expanded v-model="linkUrl" v-focus size="is-small" />
            </div>
            <div class="control">
              <b-tooltip :label="$t('link-set-tooltip')" type="is-light">
                <b-button class="button is-small" native-type="submit">
                  <span class="icon has-text-link"><i class="fas fa-link" /></span>
                </b-button>
              </b-tooltip>
            </div>
            <div class="control">
              <b-tooltip :label="$t('link-remove-tooltip')" type="is-light">
                <b-button
                  class="button is-small"
                  @click="
                    commands.link({ href: null })
                    linkMenuActive = false
                  "
                >
                  <span class="icon has-text-danger"><i class="fas fa-unlink" /></span>
                </b-button>
              </b-tooltip>
            </div>
            <div class="control">
              <b-tooltip :label="$t('link-cancel')" type="is-light">
                <b-button class="button is-small" @click="linkMenuActive = false">
                  <span class="icon"><i class="fas fa-times" /></span
                ></b-button>
              </b-tooltip>
            </div>
          </b-field>
        </form>

        <!-- Toolbar -->
        <b-field grouped v-else>
          <b-field>
            <rich-text-tool @click="commands.bold" :is-active="isActive.bold()" :label="$t('bold-button')" icon="fas fa-bold" />
            <rich-text-tool
              @click="commands.italic"
              :is-active="isActive.italic()"
              :label="$t('italic-button')"
              icon="fas fa-italic"
            />
            <rich-text-tool
              @click="showLinkMenu(getMarkAttrs('link'))"
              :is-active="isActive.link()"
              :label="$t('link-button')"
              icon="fas fa-link"
            />
          </b-field>
          <b-field>
            <rich-text-tool
              @click="commands.heading({ level: 3 })"
              :is-active="isActive.heading({ level: 3 })"
              :label="$t('h3-button')"
            >
              <strong>H3</strong>
            </rich-text-tool>
            <rich-text-tool
              @click="commands.heading({ level: 4 })"
              :is-active="isActive.heading({ level: 4 })"
              :label="$t('h4-button')"
            >
              <strong>H4</strong>
            </rich-text-tool>
            <rich-text-tool
              @click="commands.heading({ level: 5 })"
              :is-active="isActive.heading({ level: 5 })"
              :label="$t('h5-button')"
            >
              <strong>H5</strong>
            </rich-text-tool>
          </b-field>
          <b-field>
            <rich-text-tool
              @click="commands.bullet_list"
              :is-active="isActive.bullet_list()"
              :label="$t('ul-button')"
              icon="fas fa-list-ul"
            />
            <rich-text-tool
              @click="commands.ordered_list"
              :is-active="isActive.ordered_list()"
              :label="$t('ol-button')"
              icon="fas fa-list-ol"
            />
          </b-field>
        </b-field>
      </div>
    </editor-menu-bar>

    <div class="content is-custom">
      <editor-content :editor="editor" />
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import { Bold, Italic, Link, Heading, ListItem, BulletList, OrderedList, HardBreak, History } from 'tiptap-extensions'
import RichTextTool from './rich-text-tool'

export default {
  components: { EditorContent, EditorMenuBar, RichTextTool },
  props: {
    value: String
  },
  data() {
    return {
      linkMenuActive: false,
      linkUrl: null,
      blurTimeout: null,

      editor: new Editor({
        onUpdate: ({ getHTML }) => {
          this.$emit('input', getHTML())
        },
        onBlur: () => {
          this.blurTimeout = setTimeout(() => this.$emit('blur'), 250)
        },
        onFocus: () => {
          clearTimeout(this.blurTimeout)
        },
        extensions: [
          // Basic formatting
          new Bold(),
          new Italic(),
          new Link({
            openOnClick: false
          }),

          // Headings
          new Heading({
            levels: [3, 4, 5]
          }),

          // Lists
          new ListItem(),
          new BulletList(),
          new OrderedList(),

          new HardBreak(), // Support for <br>
          new History() // Support for undo/redo
        ],
        content: this.value
      })
    }
  },
  computed: {
    isSelectionEmpty() {
      return this.editor.view.state.selection.empty
    }
  },
  methods: {
    showLinkMenu(attrs) {
      this.linkMenuActive = !this.linkMenuActive
      this.linkUrl = attrs.href
    }
  },
  beforeDestroy() {
    this.editor.destroy()
  },
  watch: {
    value(newValue) {
      const isSame = this.editor.getHTML() === newValue
      if (isSame) return

      this.editor.setContent(newValue, false)
    }
  }
}
</script>

<style lang="scss" scoped>
.content {
  padding: 12px;
  border: solid 1px $grey-lighter;
  border-radius: 4px;
  box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.05);
}
</style>

<style lang="scss">
.ProseMirror {
  outline-style: none;
}
</style>
